import PrimeVue from "primevue/config";
import {definePreset} from "@primevue/themes";
import Lara from "@primevue/themes/lara";
import moment from "moment-timezone";
import {getBlogDetails} from "./helpers/index.js";
import { store } from './store'
import VueClipboard from 'vue-clipboard2'
import ToastService from "primevue/toastservice";
import ConfirmationService from 'primevue/confirmationservice';
import {createApp} from "vue";
import * as Sentry from "@sentry/vue";

import Button from "primevue/button"
import ButtonGroup from "primevue/buttongroup"
import Card from 'primevue/card';
import ConfirmDialog from 'primevue/confirmdialog';
import Tag from 'primevue/tag';
import BlockUI from 'primevue/blockui';
import Message from 'primevue/message';
import Image from 'primevue/image';
import InputGroup from 'primevue/inputgroup';
import InputGroupAddon from 'primevue/inputgroupaddon';
import InputSwitch from 'primevue/toggleswitch';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import Inplace from 'primevue/inplace';
import RadioButton from 'primevue/radiobutton';
import Dropdown from 'primevue/select';
import DataView from 'primevue/dataview';
import FileUpload from 'primevue/fileupload';
import Toast from 'primevue/toast';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Dialog from 'primevue/dialog';
import Textarea from 'primevue/textarea';
import Calendar from 'primevue/datepicker';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import Checkbox from 'primevue/checkbox';
import Chart from 'primevue/chart';
import ColorPicker from 'primevue/colorpicker';
import FloatLabel from 'primevue/floatlabel';
import Tooltip from 'primevue/tooltip';
import SelectButton from 'primevue/selectbutton';
import SplitButton from 'primevue/splitbutton';
import Badge from 'primevue/badge';
import ProgressBar from 'primevue/progressbar';
import Menu from 'primevue/menu';
import MultiSelect from 'primevue/multiselect';
import PanelMenu from 'primevue/panelmenu';
import OverlayPanel from 'primevue/popover';
import OverlayBadge from 'primevue/overlaybadge';
import AutoComplete from 'primevue/autocomplete';
import Chip from 'primevue/chip';
import Fluid from 'primevue/fluid';
import Ripple from 'primevue/ripple';
import Stepper from 'primevue/stepper';
import StepList from 'primevue/steplist';
import StepPanels from 'primevue/steppanels';
import Step from 'primevue/step';
import StepPanel from 'primevue/steppanel';
import Tabs from 'primevue/tabs';
import TabList from 'primevue/tablist';
import Tab from 'primevue/tab';
import TabPanels from 'primevue/tabpanels';
import TabPanel from 'primevue/tabpanel';
import Accordion from 'primevue/accordion';
import AccordionPanel from 'primevue/accordionpanel';
import AccordionHeader from 'primevue/accordionheader';
import AccordionContent from 'primevue/accordioncontent';
import Panel from 'primevue/panel';
import Fieldset from 'primevue/fieldset';
import Password from 'primevue/password';
import IftaLabel from 'primevue/iftalabel';
import Drawer from 'primevue/drawer';
import Divider from 'primevue/divider';

export function createAppWithTheme(App, router) {
    const app = createApp(App)
    app.use(PrimeVue, {
        theme: {
            preset: definePreset(Lara, {
                prefix: 'p',
                semantic: {
                    primary: {
                        50: '#eff6ff',
                        100: '#dbeafe',
                        200: '#bfdbfe',
                        300: '#93c5fd',
                        400: '#60a5fa',
                        500: '#3b82f6',
                        600: '#2563eb',
                        700: '#1d4ed8',
                        800: '#1e40af',
                        900: '#1e3a8a',
                        950: '#172554'
                    },
                    colorScheme: {
                        light: {
                            primary: {
                                color: '{primary.500}',
                                contrastColor: '#ffffff',
                                hoverColor: '{primary.600}',
                                activeColor: '{primary.700}'
                            },
                            highlight: {
                                background: '{primary.50}',
                                focusBackground: '{primary.100}',
                                color: '{primary.700}',
                                focusColor: '{primary.800}'
                            }
                        },
                        dark: {
                            primary: {
                                color: '{primary.400}',
                                contrastColor: '{surface.900}',
                                hoverColor: '{primary.300}',
                                activeColor: '{primary.200}'
                            },
                            highlight: {
                                background: 'color-mix(in srgb, {primary.400}, transparent 84%)',
                                focusBackground: 'color-mix(in srgb, {primary.400}, transparent 76%)',
                                color: 'rgba(255,255,255,.87)',
                                focusColor: 'rgba(255,255,255,.87)'
                            }
                        }
                    },
                    tabs: {
                        tabpanel: {
                            padding: '0.5rem 0rem 0rem 0rem !important'
                        }
                    }
                }
            }),
            options: {
                darkModeSelector: '.app-dark',
            }
        }
    })

    app.config.globalProperties.$filters = {
        formatDate(value) {
            if (value) {
                return moment(String(value)).format('MM/DD/YYYY')
            } else {
                return 'N/A'
            }
        },
        formatDateWithTime(value) {
            const timezone = getBlogDetails().timezone;
            if (value) {
                return moment(String(value)).tz(timezone).format('MM/DD/YYYY HH:mm')
            } else {
                return 'N/A'
            }
        },
        formatDateWithTimeNoTz(value) {
            if (value) {
                return moment(String(value)).format('MM/DD/YYYY HH:mm')
            } else {
                return 'N/A'
            }
        },
        ucFirst(value) {
            const splitStr = value.toLowerCase().split(' ');
            for (let i = 0; i < splitStr.length; i++) {
                splitStr[i] = splitStr[i].charAt(0).toUpperCase() + splitStr[i].substring(1);
            }
            return splitStr.join(' ');
        },
        numberFormat(value) {
            return value.toLocaleString('en-US', {maximumFractionDigits: 2})
        }
    }

    app.use(ToastService);
    app.use(store)
    app.use(VueClipboard)
    app.use(ConfirmationService);

    // Vue prime components
    app.component('Stepper', Stepper);
    app.component('StepList', StepList);
    app.component('StepPanels', StepPanels);
    app.component('Step', Step);
    app.component('StepPanel', StepPanel);

    app.component('Tabs', Tabs);
    app.component('TabList', TabList);
    app.component('Tab', Tab);
    app.component('TabPanel', TabPanel);
    app.component('TabPanels', TabPanels);

    app.component('Accordion', Accordion);
    app.component('AccordionPanel', AccordionPanel);
    app.component('AccordionHeader', AccordionHeader);
    app.component('AccordionContent', AccordionContent);

    app.component('Button', Button);
    app.component('ButtonGroup', ButtonGroup);
    app.component('Badge', Badge);
    app.component('BlockUI', BlockUI);
    app.component('Card', Card);
    app.component('ConfirmDialog', ConfirmDialog);
    app.component('Drawer', Drawer);
    app.component('Divider', Divider);
    app.component('Tag', Tag);
    app.component('Message', Message);
    app.component('MultiSelect', MultiSelect);
    app.component('Image', Image);
    app.component('IftaLabel', IftaLabel);
    app.component('InputGroup', InputGroup);
    app.component('InputGroupAddon', InputGroupAddon);
    app.component('InputSwitch', InputSwitch);
    app.component('InputText', InputText);
    app.component('InputNumber', InputNumber);
    app.component('Inplace', Inplace);
    app.component('RadioButton', RadioButton);
    app.component('Dropdown', Dropdown);
    app.component('DataView', DataView);
    app.component('FileUpload', FileUpload);
    app.component('FloatLabel', FloatLabel);
    app.component('Toast', Toast);
    app.component('DataTable', DataTable);
    app.component('Column', Column);
    app.component('Dialog', Dialog);
    app.component('Textarea', Textarea);
    app.component('Calendar', Calendar);
    app.component('IconField', IconField);
    app.component('InputIcon', InputIcon);
    app.component('Checkbox', Checkbox);
    app.component('Chart', Chart);
    app.component('ColorPicker', ColorPicker);
    app.component('SelectButton', SelectButton);
    app.component('SplitButton', SplitButton);
    app.component('ProgressBar', ProgressBar);
    app.component('Menu', Menu);
    app.component('PanelMenu', PanelMenu);
    app.component('OverlayPanel', OverlayPanel);
    app.component('OverlayBadge', OverlayBadge);
    app.component('AutoComplete', AutoComplete);
    app.component('Chip', Chip);
    app.component('Fluid', Fluid);
    app.component('Panel', Panel);
    app.component('Fieldset', Fieldset);
    app.component('Password', Password);

    app.directive('tooltip', Tooltip);
    app.directive('ripple', Ripple);

    // init sentry
    Sentry.init({
        app,
        dsn: "https://27bd298b8ded2826dd221e31c3bde6ad@o4508013426311168.ingest.us.sentry.io/4508013505740800",
        integrations: [
            Sentry.browserTracingIntegration({ router }),
            Sentry.replayIntegration(),
        ],
        // Tracing
        tracesSampleRate: 1.0, //  Capture 100% of the transactions
        // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
        tracePropagationTargets: ["devoutpost.xyz", "outpost.pub"],
        // Session Replay
        replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
        replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
        enabled: process.env.NODE_ENV === 'production'
    });

    // add router
    app.use(router)

    return app
}
